import queryString from 'qs';
import hybrid from '@zoom/hybrid-jssdk';
import { omit, trim } from 'lodash';

import { translation } from '@/plugins/i18n';
import { THEME } from '@/views/checkout/constant.js';
import { pingServerSession } from '@/apis/index.js';

import store from '@/store/index.js';

const modalURLLimitQueries = ['pageType', '_', 'modalFrom'];

const ModalGlobalEventChannels = {
  SHOW_MODAL_CLOSE: 'opc-show-modal-close',
};

// use this.$context.on to listen global event 'opc-show-modal-close', params is boolean, change this.showModalClose
export const listenModalCloseButtonShow = (cb) => {
  this.$context.$on(ModalGlobalEventChannels.SHOW_MODAL_CLOSE, (showModalClose) => {
    cb(showModalClose);
  });
};

export const triggerModalCloseButtonShow = (show) => {
  this.$context.$emit(ModalGlobalEventChannels.SHOW_MODAL_CLOSE, show);
};

const opcModalPostMessage = (params) => {
  // * use postMessage to post message to parent window
  if (window?.parent) {
    console.log('modal dialog post message: ', params);

    window.parent.postMessage(params, '*');
  }
};

export const MODAL_CHECKOUT_MESSAGE_HOOK_MAP = {
  close: 'mc_close',
  successCheckout: 'mc_success',
  openNewTab: 'mc_openNewTab', // *
  replaceCurrentPage: 'mc_replaceCurrentPage',
  showClose: 'mc_showClose',
  hiddenClose: 'mc_hiddenClose',
  showLoading: 'mc_showLoading',
  hiddenLoading: 'mc_hiddenLoading',
  sendClose: 'mc_send_close',
  switchAccount: 'mc_switch_account',
};

export const closeModal = () => {
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.close,
  });
};

export const notifySwitchAccount = () => {
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.switchAccount,
  });
};

// * export function replaceCurrentPage, params is url which is the target url
export const replaceCurrentPage = (url) => {
  console.log('modal dialog replaceCurrentPage: ', url);
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.replaceCurrentPage,
    link: url,
  });
};

export const navigateParentWindowTo = (url, closeModal = true) => {
  console.log('modal dialog navigateParentWindowTo: ', url);
  if (modalHybrid.isInWebview) {
    modalHybrid.openLinkInBrowser(url);
    if (closeModal) {
      modalHybrid.closeModal();
    }
  } else {
    replaceCurrentPage(url);
  }
};

const urlPreHandler = (url) => {
  if (modalHybrid.isInWebview) {
    // add forceSignIn for OPC url in client env
    if (url.includes('/opc/buy')) {
      return `${url}&forceSignIn=true`;
    }
  }
  return url;
};

export const navigateParentWindowToFullOPC = (closeModal = true) => {
  const url = new URL(location.href);
  const queries = queryString.parse(url.search.split('?')[1]);
  const newOPCURL = `${url.pathname}?${queryString.stringify(omit(queries, modalURLLimitQueries))}`;
  navigateParentWindowTo(newOPCURL, closeModal);
};

export const navigateParentWindowToFullOPCCheckout = () => {
  const url = new URL(location.href);
  const queries = queryString.parse(url.search.split('?')[1]);
  const newOPCURL = `/opc/buy/checkout?${queryString.stringify(omit(queries, modalURLLimitQueries))}`;
  navigateParentWindowTo(newOPCURL);
};

// export funtion which name is noticeCheckoutSuccess
// use opcModalPostmessage to post message to parent window, which params is a object with type 'mc_success''
export const noticeParentWindowCheckoutSuccess = () => {
  console.log('modal dialog noticeCheckoutSuccess');
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.successCheckout,
  });
};

// export funtion which name is show modalDialogCloseButton
// use opcModalPostmessage to post message to parent window, which params is a object with type 'mc_showClose'
export const showModalDialogCloseButton = () => {
  if (!modalHybrid.isInWebview) {
    console.log('modal dialog showModalDialogCloseButton');
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.showClose,
    });
  }
};

export const hideModalDialogCloseButton = () => {
  if (!modalHybrid.isInWebview) {
    console.log('modal dialog hideModalDialogCloseButton');
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenClose,
    });
  }
};

export const showiFrameContainerLoading = () => {
  if (!modalHybrid.isInWebview) {
    console.log('modal dialog showiFrameContainerLoading');
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.showLoading,
    });
  }
};

export const closeiFrameContainerLoading = () => {
  if (!modalHybrid.isInWebview && MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenLoading) {
    console.log('modal dialog closeiFrameContainerLoading');
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenLoading,
    });
  }
};

// * tricky patch for navigation in OPC
export const modalIframeTrickyPatch = () => {
  document.body.addEventListener(
    'click',
    function (e) {
      var target = e.target,
        targetUrl = '';
      if (target.closest('.zm-message-box__btns .zm-button--primary')) {
        var btnText = target.textContent.trim();
        var returnHomePageBtnText = translation('billing.return_home');
        if (btnText.includes(returnHomePageBtnText)) {
          targetUrl = '/';
        }
      } else if (target.closest('p.zm-message__content') && target?.href?.indexOf('/billing/report') > -1) {
        targetUrl = '/billing/report';
      } else if (target.closest('.zm-message-box__content a') && target?.href?.indexOf('/billing/payment') > -1) {
        targetUrl = '/billing/payment';
      }

      if (targetUrl) {
        e.preventDefault();
        e.stopPropagation();
        if (modalHybrid.isInWebview) {
          modalHybrid.openLinkInBrowser(targetUrl);
          modalHybrid.closeModal();
        } else {
          closeModal();
          window.top.location.href = targetUrl;
        }
      }
    },
    true,
  );
};

export const toggleTheme = function (isDark) {
  const definedTheme = isDark ? THEME.DARK : THEME.LIGHT;
  // window.themeMode = definedTheme;
  store.commit('setClientWebViewTheme', definedTheme);
  if (isDark) {
    if (!hasClass(document.body, 'zm-theme--dark')) {
      addClass(document.body, 'zm-theme--dark');
    }
    removeContainerbackgroundWhtie();
  } else {
    if (hasClass(document.body, 'zm-theme--dark')) {
      removeClass(document.body, 'zm-theme--dark');
    }
    setContainerbackgroundWhtie();
  }
};

const MATCH_MEDIA_DARK_QUERY = '(prefers-color-scheme: dark)';

// * set client webview theme
export function setClientTheme() {
  const matchMedia = window.matchMedia;
  const darkTheme = matchMedia && matchMedia(MATCH_MEDIA_DARK_QUERY);

  toggleTheme(darkTheme?.matches);
}

const setContainerbackgroundWhtie = (queries = 'html,body') => {
  [...document?.querySelectorAll(queries)].forEach((x) => (x.style.background = 'white'));
};

const removeContainerbackgroundWhtie = (queries = 'html,body') => {
  [...document?.querySelectorAll(queries)].forEach((x) => (x.style.background = null));
};

// Watch theme changed dark | light
export const watchThemeChange = (debug = false) => {
  const matchMedia = window.matchMedia;
  const darkTheme = matchMedia && matchMedia(MATCH_MEDIA_DARK_QUERY);

  if (!darkTheme.matches) {
    setContainerbackgroundWhtie();
  }

  if (debug) {
    toggleTheme(true);
  } else {
    toggleTheme(darkTheme.matches);
  }

  try {
    darkTheme?.addEventListener('change', (event) => {
      console.log('watchThemeChange=>addEventListener=>change: ', event);
      toggleTheme(event.matches);
    });
  } catch (e) {
    // For low safari
    try {
      darkTheme?.addListener(function (event) {
        console.log('watchThemeChange=>addListener: ', event);
        toggleTheme(event.matches);
      });
    } catch (e) {
      console.error('matchMedia not work, can not watchThemeChange.', e);
    }
  }
};

export function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) throw new Error('className should not contain space.');
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}

export function addClass(el, cls) {
  if (!el) return;
  let curClass = el.className;
  const classes = (cls || '').split(' ');

  for (let i = 0, j = classes.length; i < j; i++) {
    const clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else if (!hasClass(el, clsName)) {
      curClass += ' ' + clsName;
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}

export function removeClass(el, cls) {
  if (!el || !cls) return;
  const classes = cls.split(' ');
  let curClass = ' ' + el.className + ' ';

  for (let i = 0, j = classes.length; i < j; i++) {
    const clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else if (hasClass(el, clsName)) {
      curClass = curClass.replace(' ' + clsName + ' ', ' ');
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}

export const DEFAULT_MODAL_WIDTH = 688;
const DEFAULT_MODAL_HEIGHT = 715;

class ModalHybrid {
  constructor() {
    this.isInWebview = hybrid.webview.isInWebview();

    // * add modalHybrid to window in dev
    console.log('modal client: process.env.NODE_ENV: ', process.env.NODE_ENV);
    if (process.env.NODE_ENV === 'development') {
      console.log('modal client: add modal Hybrid to window in dev');
      window.modalHybrid = this;
    }

    if (this.isInWebview) {
      this.bridgeInstance = hybrid.bridge.getInstance({ config: { softRegisterBridge: true } });
      this.hybrid = hybrid;
    } else {
      console.warn('modal client: not in webview, ModalHybrid not init');
    }
  }
  registerNotificationHandler(callback) {
    if (this.isInWebview) {
      this.bridgeInstance.registerNotificationHandler(callback);
    } else {
      console.warn(`modal client: not in webview, ModalHybrid not init, registerNotificationHandler`);
    }
  }
  registerSpecificNotificationHandler(channel, callback) {
    if (this.isInWebview) {
      this.bridgeInstance.registerSpecificNotificationHandler(channel, callback);
    } else {
      console.warn(
        `modal client: not in webview, ModalHybrid not init, registerSpecificNotificationHandler ${channel} not work`,
      );
    }
  }
  openLinkInBrowserDirectly(url) {
    // postJsMessage directly to resolve the call for 'platform.getSystemParam' and 3s delay in mac client
    this.bridgeInstance.postJsMessage({
      functionName: 'common.openLinkInBrowser',
      params: {
        url,
      },
    });
  }
  openLinkInBrowser(url) {
    if (this.isInWebview) {
      console.log('modal hybrid openLinkInBrowser', url);
      try {
        new URL(url);
        // this.hybrid.common.openLinkInBrowser(urlPreHandler(url));
        this.openLinkInBrowserDirectly(urlPreHandler(url));
      } catch (error) {
        // * full path
        const hostUrl = new URL(location.href);
        // this.hybrid.common.openLinkInBrowser(urlPreHandler(`${hostUrl.protocol}//${hostUrl.host}${url}`));
        this.openLinkInBrowserDirectly(urlPreHandler(`${hostUrl.protocol}//${hostUrl.host}${url}`));
      }
    } else {
      console.warn('modal client openLinkInBrowser error, is not in client env', url);
    }
  }
  postMessage(data, needResponse = false) {
    this.bridgeInstance.postJsMessage({
      apiName: 'postMessage',
      data,
      needResponse,
    });
  }
  sendLoginMessageToClient(clientLoginParams) {
    console.log('modal client: sendLoginMessageToClient: ');
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'LoginAfterActivated',
        params: clientLoginParams,
      });
    }
  }
  notifyFreeTrialStatus(type = 0) {
    console.log('modal client: notifyFreeTrialStatus: ', type);
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'notifyFreeTrialStatus',
        params: { type },
      });
    }
  }
  resizeWebview(width = DEFAULT_MODAL_WIDTH, height = DEFAULT_MODAL_HEIGHT) {
    console.log('modal client: resizeWebview: ', width, height);
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'OnSizeNotify',
        params: {
          width,
          height,
        },
      });
    } else {
      console.warn('modal client: is not in webview, can not resizeWebview');
    }
  }
  closeModal() {
    console.log('modal client => closeWebviewModal: ');
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'CloseDialog',
      });
    } else {
      console.warn('modal client: is not in webview, can not CloseDialog');
    }
  }
}

export const modalHybrid = new ModalHybrid();

const FREE_TRIAL_INFO_SESSIN_STORAGE_KEY = 'SIGNUP/__FREE_TRIAL_INFO__';

export let getFreeTrialInfoFromSignUpActivePage = () => {
  const freeTrialInfo = JSON.parse(sessionStorage.getItem(FREE_TRIAL_INFO_SESSIN_STORAGE_KEY)) || {};
  // const {
  //   clientLoginParams,
  //   returnUrl
  // } = freeTrialInfo
  console.log('modal=>getFreeTrialInfoFromSignUpActivePage: ', freeTrialInfo);
  getFreeTrialInfoFromSignUpActivePage = () => {
    console.log('modal=>getFreeTrialInfoFromSignUpActivePage, override: ', freeTrialInfo);
    return freeTrialInfo;
  };
  return freeTrialInfo;
};

export const sendLoginMessageToClient = () => {
  const { clientLoginParams } = getFreeTrialInfoFromSignUpActivePage();

  modalHybrid.sendLoginMessageToClient(clientLoginParams);
};

export const isSignInAccountChangedInModalCheckout = () => {
  const topSBUser = window?.top?.SB?.user;
  const modalSBUser = window?.SB?.user;
  return topSBUser?.email !== modalSBUser?.email;
};

export const refreshWholePageIfSignInAccountChangedInModalCheckout = () => {
  if (isSignInAccountChangedInModalCheckout()) {
    window?.top?.location?.reload();
  }
};

export const pingServerInterval = (interval = 60 * 60 * 1000) => {
  setInterval(pingServerSession, interval);
};

export const afterPurchaseSuccessHandlerInModalCheckout = () => {
  noticeParentWindowCheckoutSuccess();
  showiFrameContainerLoading();
  refreshWholePageIfSignInAccountChangedInModalCheckout();
  showModalDialogCloseButton();
}

